import React, { FC, useState, useEffect, useCallback } from 'react';
import { style } from 'typestyle';
import { EPopup } from '@src/types/common'; 
import { setPopup } from '@src/state/popupAtoms';
import { PopupOverlay } from '@src/components/PopupOverlay';
import { PerfectScrollbar } from 'components/PerfectScrollbar';
import { ActionButton } from '@src/components/ActionButton';
import { getCrmTemplates } from '@src/queries/crmtemplates/api';
import { Services } from '@src/types/Services';
import { CRMTemplateView } from 'common';
import TemplateTable, { InlineTemplate } from './TemplateTable';
import { useMutationDeleteCRMTemplate } from '@src/queries/crmtemplates/useMutationDeleteCRMTemplate';
import { useMutationCreateCRMTemplate } from '@src/queries/crmtemplates/useMutationCreateCRMTemplate';
import { useMutationEditCRMTemplate } from '@src/queries/crmtemplates/useMutationEditCRMTemplate';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { useQueryProductsAvailableCountries } from '@src/queries/products/useQueryProducts';

interface Props {
  services: Services;
}

const CRMSettings: FC<Props> = (props) => {
  const [expandedContentIdx, setExpandedContentIdx] = useState<number | null>(null);
  const [templates, setTemplates] = useState<CRMTemplateView[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<CRMTemplateView[]>([]);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showInlineEditor, setShowInlineEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CRMTemplateView | null>(null);
  const [urlError, setUrlError] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    name?: boolean;
    content?: boolean;
    hashtag?: boolean;
  }>({});
  
  const { data: userConfig } = useQueryUserConfig();
  const { data: productCountryMappings } = useQueryProductsAvailableCountries();
  
  // Filter and sort state
  const [filters, setFilters] = useState({
    campaign: [] as string[],
    hashtag: [] as string[],
    owner: [] as string[],
    market: [] as string[],
    language: [] as string[],
    type: [] as string[],
    url: [] as string[],
    terms: [] as string[],
  });
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const [inlineValues, setInlineValues] = useState<InlineTemplate>({
    market: '',
    language: '',
    type: 'DM',
    hashtag: '',
    name: '',
    url: '',
    content: '',
    terms: '',
  });

  const { mutateAsync: mutateCreateTemplate, isPending: savingCreateTemplate } = useMutationCreateCRMTemplate();
  const { mutateAsync: mutateEditTemplate, isPending: savingEditTemplate } = useMutationEditCRMTemplate();
  const { mutateAsync: mutateDeleteTemplate, isPending: savingDeleteTemplate } = useMutationDeleteCRMTemplate();

  const refresh = useCallback(() => {
    if (!mounted) {
      return;
    }
    setLoading(true);
    getCrmTemplates()
      .then((res) => {
        const sortedTemplates = (res || []).sort((a, b) => {
          // Default sort by creation date (newest first)
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });
        setTemplates(sortedTemplates);
      })
      .finally(() => setLoading(false));
  }, [mounted]);

  const close = useCallback(() => {
    setPopup(EPopup.crmSettings, false);
  }, []);

  useEffect(() => {
    setMounted(true);
    refresh();
    return () => setMounted(false);
  }, [refresh]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...templates];

    // Apply filters
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        filtered = filtered.filter(template => {
          switch (key) {
            case 'campaign':
              return values.includes(template.name || '');
            case 'hashtag':
              return values.includes(template.hashtag || '');
            case 'owner':
              const ownerEmail = typeof template.owner === 'object' && template.owner !== null 
                ? template.owner.email || template.owner._id 
                : template.owner;
              return values.includes(ownerEmail || '');
            case 'market':
              return values.includes(template.market || '');
            case 'language':
              return values.includes(template.language || '');
            case 'type':
              return values.includes(template.type);
            case 'url':
              return values.includes(template.url || '');
            case 'terms':
              return values.includes(template.terms || '');
            default:
              return true;
          }
        });
      }
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let valueA: any, valueB: any;
      
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.createdAt);
          valueB = new Date(b.createdAt);
          break;
        case 'campaign':
          valueA = (a.name || '').toLowerCase();
          valueB = (b.name || '').toLowerCase();
          break;
        case 'hashtag':
          valueA = (a.hashtag || '').toLowerCase();
          valueB = (b.hashtag || '').toLowerCase();
          break;
        case 'owner':
          const ownerA = typeof a.owner === 'object' && a.owner !== null 
            ? a.owner.email || a.owner._id 
            : a.owner;
          const ownerB = typeof b.owner === 'object' && b.owner !== null 
            ? b.owner.email || b.owner._id 
            : b.owner;
          valueA = (ownerA || '').toLowerCase();
          valueB = (ownerB || '').toLowerCase();
          break;
        case 'market':
          valueA = (a.market || '').toLowerCase();
          valueB = (b.market || '').toLowerCase();
          break;
        case 'language':
          valueA = (a.language || '').toLowerCase();
          valueB = (b.language || '').toLowerCase();
          break;
        case 'type':
          valueA = a.type.toLowerCase();
          valueB = b.type.toLowerCase();
          break;
        case 'url':
          valueA = (a.url || '').toLowerCase();
          valueB = (b.url || '').toLowerCase();
          break;
        case 'terms':
          valueA = (a.terms || '').toLowerCase();
          valueB = (b.terms || '').toLowerCase();
          break;
        default:
          valueA = new Date(a.createdAt);
          valueB = new Date(b.createdAt);
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredTemplates(filtered);
  }, [templates, filters, sortBy, sortDirection]);

  const openAddTemplate = () => {
    setShowInlineEditor(true);
    setUrlError(false);
    setTermsError(false);
    setValidationErrors({});
    setInlineValues({
  market: '',
      language: '',
      type: 'DM',
      hashtag: '',
      name: '',
      url: '',
      content: '',
      terms: '',
    });
  };

  const handleInlineChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setInlineValues((prev) => ({ ...prev, [name]: value }));
    
    // Clear validation errors when user types
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({ ...prev, [name]: false }));
    }
    
    if (name === 'url') {
      setUrlError(!!value && !/^https?:\/\/[\w.-]+(?:\.[\w\.-]+)+(?:[\w\-\._~:/?#[\]@!$&'()*+,;=]+)?$/.test(value));
    }
    
    if (name === 'terms') {
      setTermsError(!!value && !/^https?:\/\/[\w.-]+(?:\.[\w\.-]+)+(?:[\w\-\._~:/?#[\]@!$&'()*+,;=]+)?$/.test(value));
    }
  };

  const handleInlineSave = async () => {
    try { 
      // Validate required fields
      const errors: typeof validationErrors = {};
      if (!inlineValues.name.trim()) {
        errors.name = true;
      }
      if (!inlineValues.content.trim()) {
        errors.content = true;
      }
      if (inlineValues.type === 'RR' && !inlineValues.hashtag.trim()) {
        errors.hashtag = true;
      }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }
      
      // Prepare template data, excluding hashtag for DM templates
      // Map inline.market -> country for backend
      const templateData = {
        ...inlineValues,
        country: (inlineValues as any).market,
        hashtag: inlineValues.type === 'RR' ? inlineValues.hashtag : '',
      };
      
      if (editingTemplate) {
        // For editing, use the edit mutation to preserve createdAt and owner
        await mutateEditTemplate({
          _id: editingTemplate._id,
          ...templateData,
        });
      } else {
        // For new templates, use create mutation
        await mutateCreateTemplate(templateData);
      }
      setShowInlineEditor(false);
      setEditingTemplate(null);
      setValidationErrors({});
      refresh();
    } catch (e) {
      console.error('Error saving template:', e); // Debug log
      //TODO handle error
    }
  };

  const handleInlineCancel = () => {
    setShowInlineEditor(false);
    setEditingTemplate(null);
    setValidationErrors({});
  };

  const handleEdit = async (template: CRMTemplateView) => {
    setEditingTemplate(template);
    setShowInlineEditor(true);
    setUrlError(false);
    setTermsError(false);
    setValidationErrors({});
    setInlineValues({
      market: template.market || '',
      language: template.language || '',
      type: template.type,
      hashtag: template.hashtag || '',
      name: template.name || '',
      url: template.url || '',
      content: template.content || '',
      terms: template.terms || '',
    });
  };

  const handleDelete = async (template: CRMTemplateView) => {
    setPopup(EPopup.promptconfirm, {
        question: lang.confirmDeleteTemplate,
        text: { yes: lang.delete, no: lang.cancel },
        yesColor: 'red',
        answer: async (yes) => {
          if (!yes) {
            return;
          }
          await mutateDeleteTemplate(template._id);
          refresh();
        },
      });
  };

  const handleDuplicate = async (template: CRMTemplateView) => {
    setPopup(EPopup.promptconfirm, {
        question: 'Are you sure you want to duplicate this template?',
        text: { yes: 'Duplicate', no: 'Cancel' },
        yesColor: 'blue',
        answer: async (yes) => {
          if (!yes) {
            return;
          }
          await mutateCreateTemplate({
            market: template.market,
            language: template.language,
            type: template.type,
            hashtag: template.hashtag,
            name: `Copy of ${template.name}`,
            url: template.url,
            content: template.content,
            terms: template.terms,
          });
          refresh();
        },
      });
  };

  // Insert text at cursor in content textarea
  // Generate filter options from templates
  const getFilterOptions = (field: string) => {
    const values = new Set<string>();
    templates.forEach(template => {
      let value = '';
      switch (field) {
        case 'campaign':
          value = template.name || '';
          break;
        case 'hashtag':
          value = template.hashtag || '';
          break;
        case 'owner':
          value = typeof template.owner === 'object' && template.owner !== null 
            ? template.owner.email || template.owner._id 
            : template.owner || '';
          break;
        case 'market':
          value = template.market || '';
          break;
        case 'language':
          value = template.language || '';
          break;
        case 'type':
          value = template.type;
          break;
        case 'url':
          value = template.url || '';
          break;
        case 'terms':
          value = template.terms || '';
          break;
      }
      if (value) values.add(value);
    });
    
    return Array.from(values).sort().map(value => ({
      value,
      label: value,
      selected: filters[field as keyof typeof filters].includes(value),
    }));
  };

  const handleFilterChange = (field: string, selected: string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: selected,
    }));
  };

  const handleSortChange = (newSortBy: string, direction: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(direction);
  };

  const insertAtCursor = (text: string) => {
    const textarea = document.querySelector(
      'textarea[name="content"]'
    ) as HTMLTextAreaElement | null;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setInlineValues((prev) => ({
      ...prev,
      content:
        prev.content.slice(0, start) +
        text +
        prev.content.slice(end),
    }));
    // Move cursor after inserted text
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
      textarea.focus();
    }, 0);
  };

  const handleInsertCreator = () => {
    insertAtCursor('@[creator]');
  };

  const handleInsertTerms = () => {
    insertAtCursor('[terms]');
  };

  const handleInsertHashtag = () => {
    insertAtCursor('#[hashtag]');
  };

  const handleInsertUrl = () => {
    insertAtCursor('[url]');
  };

  return (
    <PopupOverlay show={true} onClose={close} title={lang.title} width="min(95vw, 1400px)" cardClassName={cardOverrideClass} zIndex={1700}>
          <div className={descBlockClass}>
            {lang.templates}<br />
            <span className={descSubClass}>{lang.desc}</span>
          </div>
          <PerfectScrollbar className={contentClass}>
            <TemplateTable
              templates={filteredTemplates}
              loading={loading}
              showInlineEditor={showInlineEditor}
              inlineValues={inlineValues}
              urlError={urlError}
              termsError={termsError}
              expandedContentIdx={expandedContentIdx}
              savingCreateTemplate={savingCreateTemplate || savingEditTemplate}
              user={userConfig?.user}
              userConfig={userConfig}
              validationErrors={validationErrors}
              onInlineChange={handleInlineChange}
              onInlineSave={handleInlineSave}
              onInlineCancel={handleInlineCancel}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onContentToggle={setExpandedContentIdx}
              onInsertCreator={handleInsertCreator}
              onInsertTerms={handleInsertTerms}
              onInsertHashtag={handleInsertHashtag}
              onInsertUrl={handleInsertUrl}
              productCountryMappings={productCountryMappings?.res}
              // Filter and sort props
              getFilterOptions={getFilterOptions}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
              currentSortBy={sortBy}
              currentSortDirection={sortDirection}
            />
          </PerfectScrollbar>
          <div className={buttonClass}>
            <ActionButton
              variant="blue"
              size="medium"
              onAction={openAddTemplate}
              disabled={showInlineEditor}
            >
              {lang.newTemplate}
            </ActionButton>
          </div>
    </PopupOverlay>
  );
}


const cardOverrideClass = style({
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
});

const contentClass = style({
  flex: 1,
  minHeight: 0,
  paddingRight: 10,
});


const descBlockClass = style({
  textAlign: 'left',
  marginBottom: 18,
  fontSize: 16,
  color: '#ccc',
});

const descSubClass = style({
  fontSize: 13,
  color: '#aaa',
});

const buttonClass = style({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: 24,
});

const lang = {
  title: 'CRM Settings',
  templates: 'Messages templates',
  desc: 'Create and manage your templates here. You can then use them when requesting rights or sending a message via our CRM tool.',
  newTemplate: 'NEW TEMPLATE',
  loading: 'Loading...',
  confirmDeleteTemplate: 'Are you sure you want to delete this template?',
  delete: 'DELETE',
  cancel: 'CANCEL',
};

export default CRMSettings;
